import React from 'react';
import { cn } from './cn';

/**
 * AttentionStrip — a prominent, full-width call-to-action row used to surface
 * things that need the operator's action (e.g. "3 booking requests need
 * review"). Higher visual weight than InfoBanner; usually pinned near the top
 * of a page.
 *
 * Props:
 *  - icon:    optional lucide icon component
 *  - title:   bold headline
 *  - message: supporting text
 *  - action:  optional node rendered on the right (button/link)
 *  - tone:    'warning' (default) | 'primary' | 'danger' | 'info'
 */
const TONES = {
  primary: 'border-ga-primary/30 bg-ga-primary-soft',
  warning: 'border-ga-warning/30 bg-ga-warning/10',
  danger:  'border-ga-danger/30 bg-ga-danger/10',
  info:    'border-ga-info/30 bg-ga-info/10',
};

const ICON_TONES = {
  primary: 'text-ga-primary',
  warning: 'text-ga-warning',
  danger:  'text-ga-danger',
  info:    'text-ga-info',
};

export default function AttentionStrip({ icon: Icon, title, message, action, tone = 'warning', className, ...props }) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-ga-md border px-4 py-3 sm:flex-row sm:items-center sm:justify-between',
        TONES[tone] || TONES.warning,
        className
      )}
      {...props}
    >
      <div className="flex items-start gap-3 min-w-0">
        {Icon && <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', ICON_TONES[tone] || ICON_TONES.warning)} aria-hidden />}
        <div className="min-w-0">
          {title && <div className="text-sm font-semibold text-ga-text-primary">{title}</div>}
          {message && <div className="text-[13px] text-ga-text-muted">{message}</div>}
        </div>
      </div>
      {action && <div className="shrink-0 sm:pl-3">{action}</div>}
    </div>
  );
}
